import { useEffect, useRef, useState } from 'react';
import { ShipmentBanner } from 'src/components/ShipmentBanner';
import agreementImg from 'src/images/agreement.svg';
import addFileImage from 'src/images/parcel-label.svg';
import {
  AddressCard,
  AddressCardPreview,
  Button,
  Calendar,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Formik,
  FormInput,
  InputAddress,
  InputTime,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  toast,
  useApp,
  useScreenSize
} from 'straightline-ui';
import { formatNumber } from 'straightline-utils/accounting';
import {
  DOCUMENT_TYPE,
  estimateDueDate,
  isDelivered,
  isTransit,
  PROVIDER_TYPE,
  STATUS
} from 'straightline-utils/constants';
import {
  addDays,
  formatDate,
  fromZonedTime,
  isSameDay,
  startOfDay,
  toZonedTime
} from 'straightline-utils/time';

export function EtaModal({ open, toggle, shipment, onComplete }) {
  const handleOnComplete = (newEta) => {
    if (onComplete) {
      onComplete(newEta);
    }
  };

  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      contentClassName="h-100"
      modalClassName="modal-fullscreen-sm"
      size="xl"
      scrollable
      centered
      fade={false}
    >
      {() => (
        <EtaModalContent
          shipment={shipment}
          toggle={toggle}
          onComplete={handleOnComplete}
        />
      )}
    </Modal>
  );
}

function EtaModalContent({ shipment, toggle, onComplete }) {
  const app = useApp();
  const { lg } = useScreenSize();
  const scrollRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSettingStatus, setIsSettingStatus] = useState(null);
  const type = shipment.status.order <= 3 ? 'pickup' : 'delivery';
  const address = shipment[`${type}_address`];
  const addressDate =
    type === 'pickup' ? shipment.pickup_date : shipment.due_date;

  const handleSubmit = (data, formik) => {
    return app
      .service('api/shipment-etas')
      .create({
        ...data,
        type,
        shipment_id: shipment.id
      })
      .then(async (newEta) => {
        // formik.setSubmitting(false);
        app.service('api/shipments').invalidateGet(shipment.id);
        app.service('api/shipments').invalidateFind();
        app.service('api/shipment-etas').invalidateFind();
        if (onComplete) {
          return onComplete(newEta);
        }
      })
      .catch((error) => {
        if (error.formik) {
          formik.setErrors(error.formik);
        }
        formik.setSubmitting(false);
      });
  };

  const handleStatusClick = (status) => {
    setIsSettingStatus(status);

    app
      .service('api/shipments')
      .patch(shipment.id, { status_type: status })
      .then((updatedShipment) => {
        app.service('api/shipments').invalidateFind();
        app.service('api/shipments').invalidateGet(shipment.id);
        toast.success('Status updated.');
        if (onComplete) {
          return onComplete();
        }
      })
      .catch((error) => {
        console.error('Error updating status:', error);
        toast.errors(error);
        setIsSettingStatus(null);
      });
  };

  return (
    <Formik
      initialValues={{
        location: undefined,
        date: undefined,
        notes: undefined
      }}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, setValues, isSubmitting }) => {
        const isDisabled = isLoading || isSubmitting || !values.location;
        const disabledStyle = isDisabled
          ? { opacity: 0.6, pointerEvents: 'none' }
          : {};

        const handleAddressChange = async (currentLocation) => {
          if (!currentLocation) {
            setValues({
              location: undefined,
              date: undefined,
              estimatedDate: undefined,
              notes: undefined
            });

            return;
          }

          const { ...currentAddress } = currentLocation;
          delete currentAddress.place;

          setIsLoading(true);

          const [weather, route] = await Promise.allSettled([
            app.service('weather').find({
              query: {
                latitude: currentAddress.latitude,
                longitude: currentAddress.longitude,
                date: new Date()
              }
            }),
            app
              .service('api/shipment-routes')
              .find({
                query: {
                  pickup_address: currentAddress,
                  delivery_address: address,
                  departure_time: 'now'
                }
              })
              .catch(console.error)
          ]).then((responses) => {
            return responses.map((response) => response.value);
          });

          let description = [];
          let estimatedDate = null;

          description.push(
            `Driver currently in ${currentLocation.city}, ${currentLocation.state}`
          );

          if (route) {
            description.push(
              `${formatNumber(route.distance.miles)} miles to ${type} ${
                route.summary ? 'via ' + route.summary : ''
              }`
            );

            const { date } = estimateDueDate({
              route,
              delivery_address: address
            });

            estimatedDate = date;
          } else {
            let due_date = toZonedTime(new Date(), address.timezone);

            if (due_date.getHours() < 8) {
              due_date.setHours(8);
              due_date.setMinutes(0);
            } else if (due_date.getHours() > 12) {
              due_date = addDays(due_date, 1);
              due_date.setHours(8);
              due_date.setMinutes(0);
            }

            estimatedDate = fromZonedTime(due_date, address.timezone);
          }

          if (weather) {
            description.push(weather.short);
          }

          description = description.length
            ? description.join('. ') + '.'
            : null;

          estimatedDate = toZonedTime(estimatedDate, address.timezone);

          let initialDate = toZonedTime(addressDate, address.timezone);
          const today = toZonedTime(startOfDay(new Date()), address.timezone);
          if (initialDate < today) {
            initialDate = estimatedDate;
            // initialDate = new Date(today.getTime());
            // const now = toZonedTime(new Date(), address.timezone);
            // const hours = now.getHours() + 1;
            // initialDate.setHours(hours);
          } else if (isSameDay(initialDate, today)) {
            if (isSameDay(initialDate, estimatedDate)) {
              initialDate = estimatedDate;
            } else {
              const now = toZonedTime(new Date(), address.timezone);
              const hours = now.getHours() + 1;
              initialDate.setHours(hours);
            }
          } else if (isSameDay(initialDate, estimatedDate)) {
            initialDate = estimatedDate;
          } else {
            initialDate.setHours(8);
            initialDate.setMinutes(0);
          }

          initialDate = fromZonedTime(initialDate, address.timezone);
          estimatedDate = fromZonedTime(estimatedDate, address.timezone);

          setValues({
            ...values,
            date: initialDate,
            estimatedDate: estimatedDate,
            location: currentLocation,
            notes: description
          });

          setTimeout(() => {
            scrollRef.current?.scrollTo({
              behavior: 'smooth',
              top: scrollRef.current?.scrollHeight
            });
          }, 1);

          setIsLoading(false);
        };

        return (
          <Form className="modal-content h-100">
            <div className="modal-body p-3 p-sm-4" ref={scrollRef}>
              <div className="d-flex justify-content-between mb-4 mb-lg-5">
                <div>
                  <h2 className="mb-2">
                    How long before you{' '}
                    {type === 'pickup' ? 'pickup' : 'deliver'}?
                  </h2>
                  <div>
                    Set an ETA for the {type} in{' '}
                    <strong>
                      {address.city}, {address.state}
                    </strong>
                    . Include any additional notes about traffic, parking, or
                    other important details.
                  </div>
                </div>
                <div>
                  <Button type="button" close onClick={toggle}></Button>
                </div>
              </div>
              <Row>
                <Col lg="6">
                  {lg ? (
                    <AddressCard
                      map="map"
                      shipment={shipment}
                      type={type}
                      className="shadow-none mb-0"
                    />
                  ) : (
                    <AddressCardPreview
                      size="116x160"
                      address={address}
                      date={addressDate}
                      map="map"
                      className="mb-4 border rounded p-3"
                      showCurrentTime
                      showWeather
                      showAppointment
                      showDate
                    />
                  )}
                </Col>
                <Col lg="6">
                  <FormGroup>
                    <div className="d-flex justify-content-between">
                      <Label>Where is the driver currently located?</Label>
                    </div>
                    <div>
                      <InputAddress
                        placeholder="Enter a location"
                        value={values.location}
                        disabled={isSubmitting}
                        onChange={(event) => {
                          handleAddressChange(event.target.value);
                        }}
                      />
                    </div>
                  </FormGroup>
                  <div
                    className={`mb-4 mt-n3 ${values.location ? 'd-none' : ''}`}
                  >
                    {isLoading && (
                      <div className="d-flex align-items-center mb-2">
                        <div className="loader loader-sm loader-primary mr-2"></div>
                        <div className="text-primary lead animated fadeIn">
                          Collecting location data
                        </div>
                      </div>
                    )}
                    <div className="">
                      Enter the driver location to get an accurate ETA based on
                      location, weather, and route. This information will be
                      used help you complete the update.
                    </div>
                    <div className="mt-4">
                      If the driver is already at the{' '}
                      {type === 'pickup' ? 'pickup' : 'delivery'} location,
                      click the button below to automatically update the status.
                    </div>
                    <div className="mt-3">
                      <Button
                        color="primary"
                        block
                        onClick={() =>
                          handleStatusClick(
                            type === 'pickup' ? STATUS.PICKUP : STATUS.DELIVERY
                          )
                        }
                        disabled={isSettingStatus || isLoading}
                        loading={isSettingStatus === STATUS.PICKUP}
                      >
                        Driver is at {type === 'pickup' ? 'pickup' : 'delivery'}{' '}
                        location
                      </Button>
                      <Button
                        color="primary"
                        block
                        onClick={() =>
                          handleStatusClick(
                            type === 'pickup'
                              ? STATUS.TRANSIT
                              : STATUS.DELIVERED
                          )
                        }
                        disabled={isSettingStatus || isLoading}
                        loading={isSettingStatus === STATUS.DELIVERED}
                      >
                        Driver already{' '}
                        {type === 'pickup' ? 'picked up' : 'delivered'}
                      </Button>
                    </div>
                  </div>
                  <FormGroup
                    style={disabledStyle}
                    className={`mb-4 ${values.location ? '' : 'd-none'}`}
                  >
                    <Label>Notes</Label>
                    <FormInput
                      type="textarea"
                      rows="3"
                      name="notes"
                      // disabled={isDisabled}
                    />
                  </FormGroup>
                  <FormGroup
                    className={`mb-4 ${values.location ? '' : 'd-none'}`}
                  >
                    <Label>
                      {type === 'pickup' ? 'Pickup' : 'Delivery'} Date*
                    </Label>
                    <div style={disabledStyle}>
                      <Calendar
                        value={values.date}
                        className="w-100 rounded mb-4"
                        minDate={new Date()}
                        minDetail="month"
                        onChange={(newDate) => {
                          if (values.date) {
                            newDate.setHours(values.date.getHours());
                            newDate.setMinutes(values.date.getMinutes());
                          }
                          setFieldValue('date', newDate);
                        }}
                        tileClassName={({ date }) => {
                          const dateString = formatDate(date, 'dateOnly');
                          if (addressDate === dateString) {
                            return 'text-primary';
                          }
                        }}
                        tileContent={({ date }) => {
                          const dateString = formatDate(date, 'dateOnly');
                          if (addressDate === dateString) {
                            return (
                              <div
                                className="mb-n3 text-truncate"
                                style={{ fontSize: '0.75rem' }}
                              >
                                {type === 'pickup' ? 'Pickup' : 'Delivery'}
                              </div>
                            );
                          }
                        }}
                      />
                    </div>
                  </FormGroup>
                  <FormGroup
                    className={`mb-4 ${values.location ? '' : 'd-none'}`}
                  >
                    <TimeLabel type={type} address={address} />
                    <div style={disabledStyle}>
                      <Row className="mb-n3">
                        <Col className="mb-3">
                          <InputTime
                            className="d-none"
                            value={values.date}
                            onChange={([newDate]) => {
                              if (!newDate) {
                                setFieldValue('date', undefined);
                                return;
                              }
                              if (values.date) {
                                newDate.setFullYear(values.date.getFullYear());
                                newDate.setMonth(values.date.getMonth());
                                newDate.setDate(values.date.getDate());
                              }
                              setFieldValue(
                                'date',
                                fromZonedTime(newDate, address.timezone)
                              );
                            }}
                            options={{
                              inline: true,
                              // else HTML form validation (not yup) fusses when not in increment of 5
                              minuteIncrement: 1,
                              minDate: startOfDay(new Date()),
                              parseDate: (str) => {
                                return toZonedTime(str, address.timezone);
                              }
                            }}
                          />
                        </Col>
                        <Col className="mb-3">
                          <Button
                            type="button"
                            color="white"
                            disabled={
                              values.estimatedDate?.getTime() ===
                              values.date?.getTime()
                            }
                            block
                            onClick={() => {
                              setFieldValue('date', values.estimatedDate);
                            }}
                          >
                            Estimate
                          </Button>
                        </Col>
                      </Row>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </div>
            <div className="border-top p-3 p-sm-4">
              <Row className="justify-content-between align-items-center">
                <Col lg="auto">
                  <div>
                    <strong className="text-primary">
                      {values.date?.toLocaleDateString('en-US', {
                        timeZone: address.timezone,
                        timeZoneName: 'short',
                        hour: 'numeric',
                        minute: 'numeric',
                        month: 'long',
                        weekday: 'long',
                        day: 'numeric'
                      })}
                    </strong>
                  </div>
                </Col>
                <Col lg="auto" className="mt-3 mt-lg-0">
                  <div>
                    <Button
                      block
                      color="primary"
                      type="submit"
                      className="px-6"
                      disabled={isDisabled}
                      loading={isSubmitting}
                    >
                      Update ETA
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

function TimeLabel({ type, address }) {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 5000);
    return () => clearInterval(timer);
  }, []);

  let time = now
    .toLocaleDateString('en-US', {
      timeZone: address.timezone,
      timeZoneName: 'short',
      hour: 'numeric',
      minute: 'numeric'
    })
    .split(',')[1]
    .trim();

  let timezone = now
    .toLocaleDateString('en-US', {
      timeZone: address.timezone,
      timeZoneName: 'long'
    })
    .split(',')[1]
    .trim();

  return (
    <Label>
      Set the {type} time in <strong>{timezone}</strong>. <br />
      The current time in {address.city} is {time}.
    </Label>
  );
}

export function DriverModal({ open, toggle, shipment, onComplete }) {
  const [tab, setTab] = useState('dispatch');

  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      onClosed={() => setTab('dispatch')}
      contentClassName="h-100"
      modalClassName="modal-fullscreen-sm"
      size="xl"
      scrollable
      centered
      fade={false}
    >
      {() => {
        return tab === 'dispatch' ? (
          <DriverModalContent
            shipment={shipment}
            toggle={toggle}
            onComplete={() => setTab('eta')}
          />
        ) : (
          <EtaModalContent
            shipment={shipment}
            toggle={toggle}
            onComplete={onComplete}
          />
        );
      }}
    </Modal>
  );
}

function DriverModalContent({ shipment, toggle, onComplete }) {
  const app = useApp();
  const service = app.service('api/shipment-dispatches');
  const { lg } = useScreenSize();
  const [isRejected, setIsRejected] = useState(false);

  const handleSubmit = (data, formik) => {
    service
      .create(data)
      .then((result) => {
        if (result.status === 'REJECTED') {
          setIsRejected(true);
          return;
        }
        app.service('api/shipments').invalidateGet(shipment.id);
        app.service('api/shipments').invalidateFind();
        app.service('api/shipment-dispatches').invalidateFind();
        onComplete();
      })
      .catch((error) => {
        toast.errors(error);
        formik.setSubmitting(false);
        if (error.errors) {
          formik.setErrors(error.errors);
        }
      });
  };

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={service.schema}
      initialValues={{
        shipment_id: shipment.id,
        carrier_rate_type: PROVIDER_TYPE.STRAIGHTLINE,
        carrier_service_level: shipment.rate.service_level.code,
        carrier_driver: undefined,
        carrier_tracking_url: undefined,
        carrier_phone: undefined,
        carrier_truck_number: undefined,
        carrier_trailer_number: undefined
      }}
    >
      {({ isSubmitting }) => {
        const isDisabled = isSubmitting || isRejected;

        return (
          <Form className="modal-content h-100">
            <ModalBody className="p-3 p-sm-4">
              <div className="d-flex justify-content-between mb-4 mb-lg-5">
                <div>
                  <h2 className="mb-2">Assign a driver to this load.</h2>
                  <div>
                    Straightline will use this information to communicate with
                    the driver throughout the load completion.
                  </div>
                </div>
                <div>
                  <Button type="button" close onClick={toggle}></Button>
                </div>
              </div>
              <Row>
                <Col lg="6">
                  {lg ? (
                    <AddressCard
                      map="map"
                      shipment={shipment}
                      type="pickup"
                      className="shadow-none mb-0"
                    />
                  ) : (
                    <AddressCardPreview
                      map="map"
                      size="116x160"
                      address={shipment.pickup_address}
                      date={shipment.pickup_date}
                      className="border rounded mb-4 p-3"
                      showCurrentTime
                      showWeather
                      showAppointment
                      showDate
                    />
                  )}
                </Col>
                <Col lg="6">
                  <p>
                    Provide accurate driver and truck information. These details
                    are included on the Shipper's BOL and will be verified at
                    pickup and delivery locations.
                  </p>
                  <FormGroup>
                    <Label>Driver Name*</Label>
                    <FormInput name="carrier_driver" disabled={isDisabled} />
                    <FormFeedback name="carrier_driver" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Driver Phone*</Label>
                    <FormInput
                      name="carrier_phone"
                      type="phone"
                      disabled={isDisabled}
                    />
                    <FormFeedback name="carrier_phone" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Truck Number*</Label>
                    <FormInput
                      name="carrier_truck_number"
                      disabled={isDisabled}
                    />
                    <FormFeedback name="carrier_truck_number" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Trailer Number*</Label>
                    <FormInput
                      name="carrier_trailer_number"
                      disabled={isDisabled}
                    />
                    <FormFeedback name="carrier_trailer_number" />
                  </FormGroup>
                  <FormGroup>
                    <Label>Tracking URL</Label>
                    <FormInput
                      name="carrier_tracking_url"
                      disabled={isDisabled}
                    />
                    <FormFeedback name="carrier_tracking_url" />
                  </FormGroup>
                  {isRejected && (
                    <div className="alert alert-danger" role="alert">
                      There was an error dispatching this load. The Straightline
                      operations team has been notified. After reviewing the
                      details, we will reach out to you with further
                      instructions.
                    </div>
                  )}
                </Col>
              </Row>
            </ModalBody>
            <div className="border-top p-3 p-sm-4">
              <Row className="justify-content-between align-items-center">
                <Col lg="auto">
                  <div></div>
                </Col>
                <Col lg="auto" className="mt-3 mt-lg-0">
                  <div>
                    {isRejected ? (
                      <Button
                        block
                        color="white"
                        type="button"
                        className="px-6"
                        onClick={toggle}
                      >
                        Close
                      </Button>
                    ) : (
                      <Button
                        block
                        color="primary"
                        type="submit"
                        className="px-6"
                        disabled={isSubmitting}
                        loading={isSubmitting}
                      >
                        Dispatch Driver
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export function MatchedModal({ open, toggle, shipment, onComplete }) {
  const [tab, setTab] = useState('matched');

  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      onClosed={() => setTab('matched')}
      contentClassName="h-100"
      modalClassName="modal-fullscreen-sm"
      size="xl"
      scrollable
      centered
      fade={false}
    >
      {() => {
        if (tab === 'matched') {
          return (
            <MatchedModalContent
              shipment={shipment}
              toggle={toggle}
              onComplete={() => setTab('dispatch')}
            />
          );
        }
        if (tab === 'dispatch') {
          return (
            <DriverModalContent
              shipment={shipment}
              toggle={toggle}
              onComplete={() => setTab('eta')}
            />
          );
        }
        if (tab === 'eta') {
          return (
            <EtaModalContent
              shipment={shipment}
              toggle={toggle}
              onComplete={() => setTab('transfer')}
            />
          );
        }
        return (
          <RateConModalContent
            shipment={shipment}
            toggle={toggle}
            onComplete={onComplete}
            onBack={() => setTab('dispatch')}
          />
        );
      }}
    </Modal>
  );
}

function MatchedModalContent({ shipment, toggle, onComplete }) {
  return (
    <div className="modal-content h-100">
      <ModalBody className="p-3 p-sm-4">
        <div className="d-flex justify-content-between mb-4 mb-lg-5">
          <div>
            <h2 className="mb-2">Finalize Load Details</h2>
            <div>Add driver and ETA details to download rate confirmation.</div>
          </div>
          <div>
            <Button type="button" close onClick={toggle}></Button>
          </div>
        </div>
        <Row>
          <Col lg="6">
            <div className="border rounded mb-4 mb-lg-0">
              <div className="py-4 border-bottom text-center bg-primary-soft">
                <img
                  src={agreementImg}
                  alt="Shipment Booked!"
                  style={{ maxWidth: '60%' }}
                  className="img-fluid animated bounceIn faster"
                />
              </div>
              <div className="p-3 border-bottom">
                <ShipmentBanner shipment={shipment} />
              </div>
              <div className="p-3 border-bottom">
                <AddressCardPreview
                  address={shipment.pickup_address}
                  date={shipment.pickup_date}
                  showCurrentTime
                  showWeather
                />
              </div>
              <div className="p-3">
                <AddressCardPreview
                  address={shipment.delivery_address}
                  date={shipment.due_date}
                  showCurrentTime
                  showWeather
                />
              </div>
            </div>
          </Col>
          <Col lg="6">
            <h1 className="text-primary">Load #{shipment.id} Booked!</h1>
            <div className="mb-2">
              Your shipment is booked! Now, just a couple more steps to finalize
              everything. First, please dispatch a driver to handle this
              shipment. Then, set an estimated arrival time (ETA) for pickup.
              Once both of these steps are complete, you'll be able to download
              your rate confirmation.
            </div>
            <div>
              Dispatching a driver and providing an ETA helps us keep shipments
              on schedule and supports smooth communication. Thank you for your
              attention to these details!
            </div>
          </Col>
        </Row>
      </ModalBody>
      <div className="border-top p-3 p-sm-4">
        <Row className="justify-content-between align-items-center">
          <Col lg="auto"></Col>
          <Col lg="auto">
            <div>
              <Button
                block
                color="primary"
                type="submit"
                className="px-6"
                onClick={onComplete}
              >
                Continue
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

function RateConModalContent({ shipment, toggle, onComplete, onBack }) {
  const app = useApp();
  const isReady = shipment.shipment_dispatch_id;

  const { result: transfer } = app.service('api/shipment-transfers').useFindOne(
    {
      query: {
        shipment_id: shipment.id,
        $sort: { created_at: -1 }
      }
    },
    {
      keepPreviousData: true
    }
  );

  return (
    <div className="modal-content h-100">
      <div className="d-flex justify-content-between p-3 p-sm-4">
        <div>
          <h2 className="mb-2">Download Rate Confirmation</h2>
          {isReady ? (
            <span>
              The load is ready to go! Download your rate confirmation to
              continue.
            </span>
          ) : (
            <span className="text-danger">
              You cannot download the rate confirmation until you have
              dispatched this load.
            </span>
          )}
        </div>
        <div>
          <Button type="button" close onClick={toggle}></Button>
        </div>
      </div>
      <ModalBody className="p-3 p-sm-4">
        <div
          className="border rounded h-100"
          style={
            !isReady
              ? {
                  opacity: 0.4,
                  width: '100%',
                  overflow: 'hidden',
                  maskImage: 'linear-gradient(black 50%, transparent 100%)'
                }
              : {}
          }
        >
          <embed
            src={transfer?.secure_url}
            type="application/pdf"
            width="100%"
            height="100%"
            className={`${!isReady ? 'd-none d-md-block' : ''}`}
          />
        </div>
      </ModalBody>
      <div className="border-top p-3 p-sm-4">
        <Row className="justify-content-between align-items-center mb-n2">
          <Col lg="auto" className="mb-2">
            {!isReady && (
              <Button color="white" className="px-6" block onClick={onBack}>
                Dispatch Driver
              </Button>
            )}
          </Col>
          <Col lg="auto" className="mb-2">
            <div>
              <Button
                color="primary"
                className="px-6"
                block
                tag="a"
                onClick={onComplete}
                disabled={!transfer?.secure_url || !isReady}
                href={transfer?.secure_url + '?download=true'}
              >
                Download
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export function PodModal({ open, toggle, shipment, onComplete }) {
  return (
    <Modal
      isOpen={open}
      toggle={toggle}
      // contentClassName="h-100"
      modalClassName="modal-fullscreen-sm"
      // size="sm"
      scrollable
      centered
      fade={false}
    >
      {() => {
        return (
          <PodModalContent
            shipment={shipment}
            toggle={toggle}
            onComplete={onComplete}
          />
        );
      }}
    </Modal>
  );
}

function PodModalContent({ toggle, shipment, onComplete }) {
  const app = useApp();
  const [isLoading, setIsLoading] = useState(false);
  const inputRef = useRef(null);

  const handleClick = () => {
    inputRef.current.value = null;
    inputRef.current.click();
  };

  const handleImageChange = (event) => {
    setIsLoading(true);
    const files = Array.from(event.target.files);
    return Promise.all(
      files.map((file) => {
        const data = {
          file,
          name: `Shipment #${shipment.id} POD`,
          document_type: DOCUMENT_TYPE.PROOF_OF_DELIVERY
        };

        return app
          .service('api/documents')
          .create(data)
          .then((document) => {
            return app.service('api/shipment-documents').create({
              shipment_id: shipment.id,
              document_id: document.id
            });
          })
          .catch((err) => toast.errors(err));
      })
    ).then(async () => {
      if (!isDelivered(shipment)) {
        try {
          if (isTransit(shipment)) {
            await app
              .service('api/shipments')
              .patch(shipment.id, { status_type: STATUS.DELIVERY });
          }
          await app
            .service('api/shipments')
            .patch(shipment.id, { status_type: STATUS.DELIVERED });
          app.service('api/shipments').invalidateFind();
          app.service('api/shipments').invalidateGet(shipment.id);
        } catch (error) {}
      }
      toast.success('POD uploaded successfully');
      app.service('api/shipment-documents').invalidateFind();
      app.service('api/shipments').invalidateFind();
      app.service('api/shipments').invalidateGet(shipment.id);
      setIsLoading(false);
      if (onComplete) {
        onComplete();
      }
    });
  };

  return (
    <div className="modal-content h-100">
      <input
        type="file"
        accept=".jpg, .png, .pdf, .jpeg"
        multiple
        className="d-none"
        ref={inputRef}
        onChange={handleImageChange}
      />
      <ModalBody className="p-3 p-sm-4">
        <div className="d-flex justify-content-between mb-4">
          <div>
            <h2 className="mb-0">Upload POD</h2>
            Initiate payment with proof of delivery.
          </div>
          <div>
            <Button type="button" close onClick={toggle}></Button>
          </div>
        </div>
        <div className="mb-4">
          <img src={addFileImage} alt="Upload POD" className="img-fluid" />
        </div>
        <div>
          Choose a file to upload as proof of delivery for this shipment. The
          file should be a signed BOL or POD from the receiver. Please ensure
          the file is clear and legible.
        </div>
      </ModalBody>
      <ModalFooter className="p-3 p-sm-4">
        <Button
          color="primary"
          block
          onClick={handleClick}
          loading={isLoading}
          disabled={isLoading}
        >
          Upload POD
        </Button>
      </ModalFooter>
    </div>
  );
}
